// Pure rules for S23, a tenant's agents and their model profiles (spec 15 §4
// S23; D-645). The server stays the fence: these only decide what the console
// offers, and mirror the route's rules so it never offers a switch the server
// refuses.

import type { AgentModelDeployment, AgentModelProfile } from "./agents-api";
import { PlatformError } from "./platform-api";

/** The switch is platform_admin's, and a sandbox admin's only on a sandbox
 * tenant (spec 15 §7, D-477 ruling 3). */
export function canSwitchModel(
  me: { role: string; scope?: string | null },
  tenantSandbox: boolean,
): boolean {
  return me.role === "platform_admin" && (me.scope !== "sandbox" || tenantSandbox);
}

/** The profiles a switch may choose for an agent on `current`: every offered
 * profile but the one it runs on, and none the probe marked unavailable. */
export function switchTargets(
  deployment: AgentModelDeployment,
  current: string,
): readonly AgentModelProfile[] {
  return deployment.profiles.filter((p) => p.name !== current && p.availability !== "unavailable");
}

/**
 * What one day of the agent's token budget costs on a Bedrock profile with
 * every budgeted token priced at the dearer output rate, in integer
 * micro-dollars. A guide, not a hard ceiling: the budget is checked before
 * each model turn (D-530 ruling 2), so a day can run over by one turn. Null
 * for a profile with no price (`recorded`).
 */
export function dailyCeilingMicroUsd(
  dailyTokenBudget: number,
  profile: Pick<AgentModelProfile, "output_micro_usd_per_million_tokens">,
): number | null {
  const rate = profile.output_micro_usd_per_million_tokens;
  if (rate === null) return null;
  return Math.ceil((dailyTokenBudget * rate) / 1_000_000);
}

/** US dollars from integer micro-dollars: a price per million tokens or a
 * day's ceiling. Display only; nothing is computed from the string. */
export function formatUsd(microUsd: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(microUsd / 1_000_000);
}

export type SwitchRefusal =
  | "conflict"
  | "noBedrock"
  | "unknownProfile"
  | "notOffered"
  | "unavailable"
  | "forbidden"
  | "gone"
  | "failed";

/** The sentence a refused switch shows, by the problem the server answered. */
export function switchRefusal(error: unknown): SwitchRefusal {
  if (!(error instanceof PlatformError)) return "failed";
  switch (error.code) {
    case "AGENT_VERSION_CONFLICT":
      return "conflict";
    case "AGENT_PROFILE_UNKNOWN":
      return error.reason === "no_bedrock_profile" ? "noBedrock" : "unknownProfile";
    case "AGENT_PROFILE_NOT_OFFERED":
      return "notOffered";
    case "AGENT_PROFILE_UNAVAILABLE":
      return "unavailable";
    case "STAFF_SCOPE_DENIED":
    case "PERMISSION_DENIED":
      return "forbidden";
    case "AGENT_NOT_FOUND":
      return "gone";
    default:
      return error.status === 404 ? "gone" : "failed";
  }
}

/** A refusal that means the screen is out of date and must read again. */
export function refusalRereads(refusal: SwitchRefusal): boolean {
  return refusal === "conflict" || refusal === "unavailable" || refusal === "gone";
}
